import { cn } from '../../lib/cn';
import type { NavBadges, NavItem } from './nav';
import { badgeText, badgeWords } from './useNavBadges';

/**
 * A count on a nav item, 18px tall, placed by the caller: at the top right of an icon or a tile, or at
 * the end of a sidebar row. Unread messages are the New Content green; Digest Sources failing their
 * pulls are the warning amber. Drawn only, with the words beside it in `sr-only` text (NavItemLabel),
 * so it is not read twice.
 *
 * Sized in `--nav-px`, so in the tablet rail, which sets it, it is exactly 18px. Everywhere else it
 * falls back to 1/16rem and scales with the UI as before.
 */
export function NavBadge({
  kind,
  count,
  className,
}: {
  kind: keyof NavBadges;
  count: number;
  className?: string;
}) {
  if (count <= 0) return null;
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none flex h-[calc(18*var(--nav-px,0.0625rem))] min-w-[calc(18*var(--nav-px,0.0625rem))] items-center justify-center rounded-full px-[calc(4*var(--nav-px,0.0625rem))] text-[length:calc(11*var(--nav-px,0.0625rem))] font-semibold leading-none tabular-nums',
        kind === 'messages' ? 'bg-create text-white' : 'bg-warning-dark text-chrome-bar',
        className,
      )}
    >
      {badgeText(count)}
    </span>
  );
}

/**
 * A nav item's label. Where the item is drawn narrow (`short`: the rail and the bottom bar) it shows
 * its short label, if it has one; either way a screen reader hears the full label, then the badge's
 * words while it has a count.
 */
export function NavItemLabel({
  item,
  count,
  short = false,
  className,
}: {
  item: NavItem;
  count: number;
  short?: boolean;
  className?: string;
}) {
  const shown = (short && item.short) || item.label;
  const words = item.badge && count > 0 ? `, ${badgeWords(item.badge, count)}` : '';
  if (shown === item.label && !words) return <span className={className}>{item.label}</span>;
  return (
    <>
      <span aria-hidden className={className}>
        {shown}
      </span>
      <span className="sr-only">
        {item.label}
        {words}
      </span>
    </>
  );
}
